/**
 * Renders markdown-formatted text as HTML.
 * Used for content created with the admin rich text toolbar.
 */

import { httpUrl } from "@/lib/publicOrganizationProfile";

interface RichTextRendererProps {
  content: string;
  className?: string;
}

const PROSE_CLASSES = "prose prose-sm max-w-none prose-headings:text-foreground prose-p:text-muted-foreground prose-strong:text-foreground prose-em:text-muted-foreground prose-a:text-primary prose-li:text-muted-foreground prose-blockquote:text-muted-foreground prose-blockquote:border-primary/30 prose-hr:border-border";

// The @tailwindcss/typography plugin is not registered in tailwind.config.ts so the prose
// classes above produce no paragraph spacing. These explicit selectors restore vertical
// rhythm without depending on the plugin.
const SPACING_CLASSES = "[&_p]:mb-4 [&_h2]:mt-6 [&_h2]:mb-2 [&_ul]:mb-4 [&_ol]:mb-4 [&_blockquote]:my-4 [&_li]:mb-1 [&_hr]:my-6";

function unescapeHtml(value: string): string {
  return value
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&gt;/g, ">")
    .replace(/&lt;/g, "<")
    .replace(/&amp;/g, "&");
}

function escapeAttribute(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * The href for a markdown link target as the author typed it, or null when it
 * must render as label text. Absolute http(s) URLs pass only through httpUrl(),
 * the same check every other admin-typed URL on public pages uses. mailto: is
 * not a link either: these pages are public and crawlable, and agents reach an
 * organization through Message.
 */
function safeLinkHref(target: string): string | null {
  return httpUrl(target);
}

function applyEmphasis(value: string): string {
  return value
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/_(.+?)_/g, "<em>$1</em>");
}

export function markdownToHtml(md: string): string {
  let html = md
    // Escape HTML first, quotes included, so nothing the author typed can open a
    // tag or leave an attribute.
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

  // Horizontal rules
  html = html.replace(/^---$/gm, "<hr />");

  // Headings (## only, as toolbar supports H2)
  html = html.replace(/^## (.+)$/gm, "<h2>$1</h2>");

  // Blockquotes
  html = html.replace(/^&gt; (.+)$/gm, "<blockquote>$1</blockquote>");

  // Links: only absolute http(s) targets (via httpUrl) become links. Anything
  // else (mailto:, javascript:, data:, //host, a relative path) keeps its
  // label as plain text. The target was HTML-escaped above, so it is decoded for
  // validation and escaped again for the attribute. Links run before bold and
  // italic, and each rendered anchor is parked behind a placeholder, so an `_`
  // or `**` in a URL never becomes emphasis inside the href. The placeholder
  // starts with a raw `<`, which the escaping above leaves no author text able
  // to contain.
  const anchors: string[] = [];
  html = html.replace(/\[(.+?)\]\((.+?)\)/g, (_match, label: string, href: string) => {
    const safe = safeLinkHref(unescapeHtml(href).trim());
    if (!safe) return label;
    anchors.push(
      `<a href="${escapeAttribute(safe)}" target="_blank" rel="noopener noreferrer">${applyEmphasis(label)}</a>`,
    );
    return `<@${anchors.length - 1}>`;
  });

  // Bold and italic
  html = applyEmphasis(html);

  html = html.replace(/<@(\d+)>/g, (_match, index: string) => anchors[Number(index)]);

  // Numbered lists: consecutive lines starting with "N. "
  html = html.replace(/((?:^\d+\. .+$\n?)+)/gm, (match) => {
    const items = match.trim().split("\n").map(line =>
      `<li>${line.replace(/^\d+\. /, "")}</li>`
    ).join("");
    return `<ol>${items}</ol>`;
  });

  // Bullet lists: consecutive lines starting with "- "
  html = html.replace(/((?:^- .+$\n?)+)/gm, (match) => {
    const items = match.trim().split("\n").map(line =>
      `<li>${line.replace(/^- /, "")}</li>`
    ).join("");
    return `<ul>${items}</ul>`;
  });

  // Wrap remaining plain lines in <p> tags
  html = html
    .split("\n")
    .map(line => {
      const trimmed = line.trim();
      if (!trimmed) return "";
      if (/^<(h[1-6]|ul|ol|li|blockquote|hr|p)/.test(trimmed)) return trimmed;
      return `<p>${trimmed}</p>`;
    })
    .join("");

  return html;
}

const RichTextRenderer = ({ content, className = "" }: RichTextRendererProps) => {
  if (!content) return null;

  // Check if content contains any markdown syntax
  const hasMarkdown = /(\*\*|__|^##\s|^- |^\d+\.\s|^>\s|\[.+\]\(.+\)|^---$)/m.test(content);

  if (!hasMarkdown) {
    // Plain text — just render as-is (caller handles display)
    return (
      <div
        className={`${PROSE_CLASSES} ${SPACING_CLASSES} ${className}`}
        dangerouslySetInnerHTML={{ __html: `<p>${content.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n\n/g, "</p><p>").replace(/\n/g, "<br />")}</p>` }}
      />
    );
  }

  const html = markdownToHtml(content);

  return (
    <div
      className={`${PROSE_CLASSES} ${SPACING_CLASSES} ${className}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

export default RichTextRenderer;
